import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  MessageSquare,
  LogOut,
  UserCircle,
  Brain,
  Mic,
  Menu,
  X,
} from 'lucide-react';
import { useBranding } from '../contexts/BrandingContext';

import { cn, API_BASE_URL } from '../lib/utils';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { customerAiChatAPI } from '../lib/customerApi';
import ThemeToggle from './ThemeToggle';

const AI_UNREAD_EVENT = 'customer:ai_chat_unread';

const CustomerLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useCustomerAuth();
  const { branding } = useBranding();
  const [aiUnread, setAiUnread] = useState(0);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const refreshAiUnread = useCallback(async () => {
    const token = localStorage.getItem('customer_token');
    if (!token || !location.pathname.startsWith('/customers')) return;
    try {
      const { data } = await customerAiChatAPI.getUnread();
      setAiUnread(Math.max(0, Number(data?.unread_count) || 0));
    } catch {
      setAiUnread(0);
    }
  }, [location.pathname]);

  useEffect(() => {
    refreshAiUnread();
    const t = setInterval(refreshAiUnread, 45000);
    const onFocus = () => refreshAiUnread();
    const onCustom = (e) => {
      if (e?.detail && typeof e.detail.unread_count === 'number') {
        setAiUnread(Math.max(0, e.detail.unread_count));
      } else {
        refreshAiUnread();
      }
    };
    window.addEventListener('focus', onFocus);
    window.addEventListener(AI_UNREAD_EVENT, onCustom);
    return () => {
      clearInterval(t);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener(AI_UNREAD_EVENT, onCustom);
    };
  }, [refreshAiUnread]);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileNavOpen]);

  const navItems = [
    { icon: MessageSquare, label: 'Chat', path: '/customers/chat', badge: null },
    { icon: Brain, label: 'AI Chat', path: '/customers/ai-chat', badge: aiUnread },
    { icon: Mic, label: 'Voice AI', path: '/customers/ai-voice-chat', badge: null },
  ];

  const handleLogout = () => {
    logout();
    navigate('/customers/login');
  };

  const closeMobileNav = () => setMobileNavOpen(false);

  const brandMark = branding?.logo_url ? (
    <img
      src={`${API_BASE_URL}${branding.logo_url}?v=${encodeURIComponent(String(branding.rev || 0))}`}
      alt={branding?.brand_name || 'Logo'}
      className="w-9 h-9 rounded object-contain"
    />
  ) : (
    <div className="w-9 h-9 rounded-md bg-primary flex items-center justify-center">
      <UserCircle className="w-5 h-5 text-white" />
    </div>
  );

  const sidebar = (
    <>
      <div className="flex items-center justify-between gap-2 px-2 py-2">
        <div className="flex items-center gap-3 min-w-0">
          {brandMark}
          <div className="min-w-0">
            <div className="font-semibold text-foreground truncate">
              {branding?.brand_name || user?.name || 'Customer'}
            </div>
            <div className="text-xs text-muted-foreground truncate">
              {user?.email}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <div className="hidden md:block">
            <ThemeToggle />
          </div>
          <button
            type="button"
            className="md:hidden p-2 rounded-lg hover:bg-muted text-muted-foreground"
            onClick={closeMobileNav}
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      <nav className="mt-6 flex-1 overflow-y-auto min-h-0">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={closeMobileNav}
              className={cn(
                'nav-item flex items-center gap-3 px-3 py-2.5 rounded-md mb-1 transition-colors relative',
                isActive
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
            >
              <item.icon className="w-5 h-5 shrink-0" strokeWidth={1.5} />
              <span className="text-sm font-semibold flex-1 min-w-0">
                {item.label}
              </span>
              {item.badge > 0 ? (
                <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-violet-600 text-white text-[11px] font-bold flex items-center justify-center leading-none">
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              ) : null}
            </Link>
          );
        })}

        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md mt-4 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        >
          <LogOut className="w-5 h-5" strokeWidth={1.5} />
          <span className="text-sm font-semibold">Logout</span>
        </button>
      </nav>

      <div className="md:hidden pt-3 border-t border-border mt-4">
        <ThemeToggle className="w-full justify-center" />
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background">
      <header className="md:hidden fixed top-0 inset-x-0 z-40 h-14 bg-card border-b border-border flex items-center px-3 gap-2">
        <button
          type="button"
          onClick={() => setMobileNavOpen(true)}
          className="p-2 rounded-lg hover:bg-muted text-foreground shrink-0"
          aria-label="Open menu"
        >
          <Menu className="w-5 h-5" />
        </button>
        {brandMark}
        <span className="text-sm font-semibold text-foreground truncate">
          {branding?.brand_name || user?.name || 'Customer'}
        </span>
      </header>

      {mobileNavOpen && (
        <button
          type="button"
          className="md:hidden fixed inset-0 z-40 bg-black/50"
          aria-label="Close menu overlay"
          onClick={closeMobileNav}
        />
      )}

      <div className="flex min-h-screen">
        <aside
          className={cn(
            'w-[260px] bg-card border-r border-border p-4 flex flex-col shrink-0',
            'fixed md:static inset-y-0 left-0 z-50 h-screen',
            'transition-transform duration-300 ease-in-out',
            mobileNavOpen
              ? 'translate-x-0'
              : '-translate-x-full md:translate-x-0'
          )}
        >
          {sidebar}
        </aside>

        <main className="flex-1 min-w-0 pt-14 md:pt-0">
          <div className="p-3 sm:p-4 h-[calc(100vh-3.5rem)] md:h-screen overflow-hidden">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default CustomerLayout;
